import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
} from '@mui/material';
import { PersonOutline, DirectionsCar, Business, Email, Close, ContentCopy, Delete } from '@mui/icons-material';
import { collection, query, where, getDocs, doc, updateDoc, orderBy, limit } from 'firebase/firestore';
import { MainLayout } from '../components/layout/MainLayout';
import { db } from '../lib/firebase';
import { DriverProfile, UserEmailResponse } from '../types/driver';
import { useAuth } from '../contexts/AuthContext';

export const DriverPage: React.FC = () => {
  const { adminType } = useAuth();
  const [drivers, setDrivers] = useState<DriverProfile[]>([]);
  const [driverAppVersions, setDriverAppVersions] = useState<Record<string, string>>({});
  const [driverLastOperationDates, setDriverLastOperationDates] = useState<Record<string, string>>({});
  const [latestAppVersion, setLatestAppVersion] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [updatingDriverId, setUpdatingDriverId] = useState<string | null>(null);

  // 이메일 다이얼로그 관련 상태
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState<DriverProfile | null>(null);
  const [emailCache, setEmailCache] = useState<Record<string, string>>({});
  const [loadingEmail, setLoadingEmail] = useState(false);

  // 삭제 다이얼로그 관련 상태
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [driverToDelete, setDriverToDelete] = useState<DriverProfile | null>(null);
  const [deleting, setDeleting] = useState(false);

  const parseVersionSegments = (version: string): number[] => {
    const versionCore = version.trim().replace(/^v/i, '').split(/[+-]/)[0];
    return versionCore.split('.').map((segment) => {
      const value = parseInt(segment.replace(/\D/g, ''), 10);
      return Number.isNaN(value) ? 0 : value;
    });
  };

  const compareVersions = (a: string, b: string): number => {
    const aSegments = parseVersionSegments(a);
    const bSegments = parseVersionSegments(b);
    const length = Math.max(aSegments.length, bSegments.length);

    for (let index = 0; index < length; index += 1) {
      const aValue = aSegments[index] || 0;
      const bValue = bSegments[index] || 0;

      if (aValue !== bValue) {
        return aValue - bValue;
      }
    }

    return 0;
  };

  const isOutdatedAppVersion = (version: string): boolean => {
    if (!latestAppVersion || !version || version === '-' || version === '조회 실패') {
      return false;
    }

    return compareVersions(version, latestAppVersion) < 0;
  };

  // 운전자 목록 조회
  useEffect(() => {
    const fetchLatestAppVersion = async () => {
      const snapshot = await getDocs(collection(db, 'app_status'));
      const statusDoc = snapshot.docs.find((doc) => typeof doc.data().lastVersion === 'string');
      return statusDoc?.data().lastVersion || '';
    };

    const fetchLatestDriverOperationInfo = async (drivers: DriverProfile[]) => {
      const operationInfoEntries = await Promise.all(
        drivers.map(async (driver) => {
          try {
            const latestOperationQuery = query(
              collection(db, 'operations'),
              where('driverUid', '==', driver.uid),
              orderBy('__name__', 'desc'),
              limit(1)
            );
            const snapshot = await getDocs(latestOperationQuery);

            if (snapshot.empty) {
              return [driver.id, { usedVersion: '-', operationDate: '-' }] as const;
            }

            const latestOperationDoc = snapshot.docs[0];
            const latestOperation = latestOperationDoc.data();
            return [
              driver.id,
              {
                usedVersion: latestOperation.usedVersion || '-',
                operationDate: latestOperation.operationDate || latestOperationDoc.id.split('_')[0] || '-',
              },
            ] as const;
          } catch (error) {
            console.error(`Error fetching latest operation info for driver ${driver.id}:`, error);
            return [driver.id, { usedVersion: '조회 실패', operationDate: '조회 실패' }] as const;
          }
        })
      );

      return Object.fromEntries(operationInfoEntries);
    };

    const fetchDrivers = async () => {
      setLoading(true);
      try {
        let q = query(collection(db, 'driver_profiles'));

        // 권한별 필터링
        if (adminType === 'route1') {
          q = query(collection(db, 'driver_profiles'), where('organization_name', '==', '부산은성관광'));
        } else if (adminType === 'route2') {
          q = query(collection(db, 'driver_profiles'), where('organization_name', '==', '뉴대림고속관광'));
        }

        const snapshot = await getDocs(q);
        const driverData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as DriverProfile[];

        // isWorking이 false인 운전자 필터링 (undefined인 경우 true로 간주)
        const activeDrivers = driverData.filter(
          (driver) => driver.isWorking !== false
        );

        // 정렬: 1) 미등록 운전자 우선, 2) 이름순
        activeDrivers.sort((a, b) => {
          // 등록 상태로 먼저 정렬 (미등록이 먼저)
          if (a.isLicenced !== b.isLicenced) {
            return a.isLicenced ? 1 : -1;
          }
          // 같은 등록 상태면 이름순 정렬
          return a.name.localeCompare(b.name, 'ko');
        });
        setDrivers(activeDrivers);
        const [lastVersion, latestOperationInfo] = await Promise.all([
          fetchLatestAppVersion(),
          fetchLatestDriverOperationInfo(activeDrivers),
        ]);
        setLatestAppVersion(lastVersion);
        setDriverAppVersions(
          Object.fromEntries(
            Object.entries(latestOperationInfo).map(([driverId, info]) => [driverId, info.usedVersion])
          )
        );
        setDriverLastOperationDates(
          Object.fromEntries(
            Object.entries(latestOperationInfo).map(([driverId, info]) => [driverId, info.operationDate])
          )
        );
      } catch (error) {
        console.error('Error fetching drivers:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDrivers();
  }, [adminType]);

  // 이메일 조회 함수
  const fetchUserEmail = async (uid: string): Promise<string> => {
    try {
      const response = await fetch(
        'https://us-central1-bpnpshuttle.cloudfunctions.net/getUserEmailHttp',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ uid }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch email');
      }

      const data: UserEmailResponse = await response.json();
      return data.email;
    } catch (error) {
      console.error('Error fetching email:', error);
      throw error;
    }
  };

  // 이메일 보기 버튼 핸들러
  const handleViewEmail = async (driver: DriverProfile) => {
    setSelectedDriver(driver);
    setEmailDialogOpen(true);

    // 이미 캐시된 이메일이 있으면 조회하지 않음
    if (emailCache[driver.id]) {
      return;
    }

    // 이메일 조회
    setLoadingEmail(true);
    try {
      const email = await fetchUserEmail(driver.uid);
      setEmailCache((prev) => ({ ...prev, [driver.id]: email }));
    } catch (error) {
      console.error('Error fetching email:', error);
      setEmailCache((prev) => ({ ...prev, [driver.id]: '조회 실패' }));
    } finally {
      setLoadingEmail(false);
    }
  };

  // 다이얼로그 닫기
  const handleCloseDialog = () => {
    setEmailDialogOpen(false);
    setSelectedDriver(null);
  };

  // 이메일 복사
  const handleCopyEmail = () => {
    if (selectedDriver && emailCache[selectedDriver.id]) {
      navigator.clipboard.writeText(emailCache[selectedDriver.id]);
      alert('이메일이 클립보드에 복사되었습니다.');
    }
  };

  // 등록 상태 토글
  const handleToggleLicence = async (driver: DriverProfile) => {
    setUpdatingDriverId(driver.id);
    try {
      const newStatus = !driver.isLicenced;
      await updateDoc(doc(db, 'driver_profiles', driver.id), {
        isLicenced: newStatus,
        updatedAt: new Date(),
      });

      // 로컬 상태 업데이트
      setDrivers((prev) =>
        prev.map((d) => (d.id === driver.id ? { ...d, isLicenced: newStatus } : d))
      );
    } catch (error) {
      console.error('Error updating licence status:', error);
      alert('등록 상태 변경에 실패했습니다.');
    } finally {
      setUpdatingDriverId(null);
    }
  };

  // 삭제 버튼 클릭 핸들러
  const handleDeleteClick = (driver: DriverProfile) => {
    setDriverToDelete(driver);
    setDeleteDialogOpen(true);
  };

  // 삭제 확인 핸들러
  const handleConfirmDelete = async () => {
    if (!driverToDelete) return;

    setDeleting(true);
    try {
      await updateDoc(doc(db, 'driver_profiles', driverToDelete.id), {
        isLicenced: false,
        isWorking: false,
        updatedAt: new Date(),
      });

      // 로컬 상태에서 제거 (isWorking이 false이므로 목록에서 사라짐)
      setDrivers((prev) => prev.filter((d) => d.id !== driverToDelete.id));
      
      setDeleteDialogOpen(false);
      setDriverToDelete(null);
      alert('운전자가 삭제되었습니다.');
    } catch (error) {
      console.error('Error deleting driver:', error);
      alert('운전자 삭제에 실패했습니다.');
    } finally {
      setDeleting(false);
    }
  };

  // 삭제 다이얼로그 닫기
  const handleCloseDeleteDialog = () => {
    if (!deleting) {
      setDeleteDialogOpen(false);
      setDriverToDelete(null);
    }
  };

  // 삭제 가능 여부 확인 (노선 관리자이고 자신의 노선 운전자인 경우만)
  const canDeleteDriver = (driver: DriverProfile): boolean => {
    if (adminType === 'super') {
      return false; // 최고 관리자는 삭제 불가
    }
    if (adminType === 'route1') {
      return driver.organization_name === '부산은성관광';
    }
    if (adminType === 'route2') {
      return driver.organization_name === '뉴대림고속관광';
    }
    return false;
  };

  return (
    <MainLayout>
      <Box>
        {/* 헤더 */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonOutline sx={{ fontSize: 32, color: '#667eea' }} />
            <Box>
              <Typography variant="h4" component="h1" fontWeight={700}>
                운전자 관리
              </Typography>
              <Typography variant="body2" color="text.secondary">
                셔틀버스 운전자 목록 및 등록 상태를 관리합니다.
              </Typography>
            </Box>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* 통계 카드 */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 3 }}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <PersonOutline sx={{ fontSize: 32, color: '#667eea' }} />
                  <Typography variant="h6" fontWeight={600}>
                    총 운전자
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} color="primary">
                  {drivers.length}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  등록된 운전자 수입니다.
                </Typography>
              </Paper>

              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <DirectionsCar sx={{ fontSize: 32, color: '#10b981' }} />
                  <Typography variant="h6" fontWeight={600}>
                    등록 완료
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} sx={{ color: '#10b981' }}>
                  {drivers.filter((d) => d.isLicenced).length}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {drivers.length > 0
                    ? ((drivers.filter((d) => d.isLicenced).length / drivers.length) * 100).toFixed(1)
                    : 0}
                  % 등록됨
                </Typography>
              </Paper>

              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Business sx={{ fontSize: 32, color: '#764ba2' }} />
                  <Typography variant="h6" fontWeight={600}>
                    미등록
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} sx={{ color: '#f59e0b' }}>
                  {drivers.filter((d) => !d.isLicenced).length}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  등록 대기 중입니다.
                </Typography>
              </Paper>
            </Box>

            {/* 운전자 목록 테이블 */}
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  운전자 목록
                </Typography>
                {latestAppVersion && (
                  <Chip
                    label={`최신 기사앱 ${latestAppVersion}`}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>

              {drivers.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography color="text.secondary">운전자 정보가 없습니다.</Typography>
                </Box>
              ) : (
                <TableContainer sx={{ mt: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>이름</TableCell>
                        <TableCell>소속</TableCell>
                        <TableCell>차량 번호</TableCell>
                        <TableCell align="center">마지막 운행일</TableCell>
                        <TableCell align="center">기사앱 버전</TableCell>
                        <TableCell align="center">이메일</TableCell>
                        <TableCell align="center">등록 상태</TableCell>
                        <TableCell align="center">상태 변경</TableCell>
                        <TableCell align="center">삭제</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {drivers.map((driver) => (
                        <TableRow key={driver.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PersonOutline sx={{ fontSize: 20, color: '#667eea' }} />
                              <Typography variant="body2" fontWeight={600}>
                                {driver.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Business sx={{ fontSize: 18, color: '#764ba2' }} />
                              <Typography variant="body2">{driver.organization_name}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <DirectionsCar sx={{ fontSize: 18, color: '#10b981' }} />
                              <Typography variant="body2" fontWeight={600}>
                                {driver.vehicleNumber}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight={600}>
                              {driverLastOperationDates[driver.id] || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                color={isOutdatedAppVersion(driverAppVersions[driver.id]) ? 'error' : 'text.primary'}
                              >
                                {driverAppVersions[driver.id] || '-'}
                              </Typography>
                              {isOutdatedAppVersion(driverAppVersions[driver.id]) && (
                                <Chip
                                  label="업데이트 필요"
                                  color="error"
                                  size="small"
                                  sx={{
                                    height: 22,
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                  }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Email />}
                              onClick={() => handleViewEmail(driver)}
                            >
                              이메일 보기
                            </Button>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={driver.isLicenced ? '등록됨' : '미등록'}
                              color={driver.isLicenced ? 'success' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Switch
                              checked={driver.isLicenced}
                              onChange={() => handleToggleLicence(driver)}
                              disabled={updatingDriverId === driver.id}
                              color="success"
                            />
                            {updatingDriverId === driver.id && (
                              <CircularProgress size={20} sx={{ ml: 1 }} />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              startIcon={<Delete />}
                              onClick={() => handleDeleteClick(driver)}
                              disabled={!canDeleteDriver(driver)}
                            >
                              삭제
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>

            {/* 이메일 다이얼로그 */}
            <Dialog open={emailDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
              <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Email sx={{ color: '#667eea' }} />
                    <Typography variant="h6" fontWeight={600}>
                      운전자 이메일
                    </Typography>
                  </Box>
                  <IconButton onClick={handleCloseDialog} size="small">
                    <Close />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent>
                {selectedDriver && (
                  <Box>
                    <Box sx={{ mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        운전자 이름
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {selectedDriver.name}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        이메일 주소
                      </Typography>
                      {loadingEmail ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <CircularProgress size={20} />
                          <Typography variant="body2" color="text.secondary">
                            이메일 조회 중...
                          </Typography>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 2,
                            border: '1px solid #e2e8f0',
                            borderRadius: 1,
                            bgcolor: '#fff',
                          }}
                        >
                          <Typography variant="body1" fontWeight={600} sx={{ flex: 1 }}>
                            {emailCache[selectedDriver.id] || '조회 중...'}
                          </Typography>
                          {emailCache[selectedDriver.id] && emailCache[selectedDriver.id] !== '조회 실패' && (
                            <IconButton size="small" onClick={handleCopyEmail} color="primary">
                              <ContentCopy fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <Button onClick={handleCloseDialog} variant="contained">
                  닫기
                </Button>
              </DialogActions>
            </Dialog>

            {/* 삭제 확인 다이얼로그 */}
            <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth>
              <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Delete sx={{ color: '#ef4444' }} />
                    <Typography variant="h6" fontWeight={600}>
                      운전자 삭제 확인
                    </Typography>
                  </Box>
                  <IconButton onClick={handleCloseDeleteDialog} size="small" disabled={deleting}>
                    <Close />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent>
                {driverToDelete && (
                  <Box>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      운전자를 삭제하면 목록에서 제외되며, 기존 운행 정보는 유지됩니다.
                    </Alert>
                    <Box sx={{ mb: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        운전자 이름
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {driverToDelete.name}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        소속
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {driverToDelete.organization_name}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        차량 번호
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {driverToDelete.vehicleNumber}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      정말 이 운전자를 삭제하시겠습니까?
                    </Typography>
                  </Box>
                )}
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <Button onClick={handleCloseDeleteDialog} disabled={deleting}>
                  취소
                </Button>
                <Button
                  onClick={handleConfirmDelete}
                  variant="contained"
                  color="error"
                  disabled={deleting}
                  startIcon={deleting ? <CircularProgress size={20} /> : <Delete />}
                >
                  {deleting ? '삭제 중...' : '삭제'}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Box>
    </MainLayout>
  );
};
